/* GridRowRenderer.js */

"use strict";

//const _ = require('lodash');
const GridCell = require('../cell/GridCellComponent');

class GridRowRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("grid-row");
        this._rootElem.component = this._model;
        this._rootElem.tabIndex = -1;

        this._cells = [];

        this._registerListeners();
        this.render();
    }
    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/
    _shutdown(){
        this._unregisterListeners();

        let promArr = [Promise.resolve()];

        this._cells.forEach((cell) => {
            cell.removeAllListeners('CellAction');
            promArr.push(cell.shutdown());
        });

        return Promise.all(promArr)
            .then(()=>{
                this._model = undefined;
                this._rootElem.remove();
                this._textField = undefined;
                this._rootElem = undefined;
                this._cells = undefined;
            });
    }
    _registerListeners(){
        this._model.addListener("ID",this._onId = this._onId.bind(this));
        this._model.addListener("RowData",this._onRowData = this._onRowData.bind(this));
        this._model.addListener("ColumnOrder",this._onColumnOrder = this._onColumnOrder.bind(this));
        this._model.addListener("ColumnComponents",this._onColumnComponents = this._onColumnComponents.bind(this));
        this._model.addListener("Selected",this._onSelected = this._onSelected.bind(this));
        this._rootElem.addEventListener('click', this._onRowClick = this._onRowClick.bind(this));
        this._rootElem.addEventListener('dblclick', this._onRowDoubleClick = this._onRowDoubleClick.bind(this));
        this._rootElem.addEventListener('mouseenter', this._onMouseEnter = this._onMouseEnter.bind(this));
        this._rootElem.addEventListener('mouseleave', this._onMouseLeave = this._onMouseLeave.bind(this));
    }
    _unregisterListeners(){
        this._model.removeListener("ID",this._onId);
        this._model.removeListener("RowData",this._onRowData);
        this._model.removeListener("ColumnOrder",this._onColumnOrder);
        this._model.removeListener("ColumnComponents",this._onColumnComponents);
        this._model.removeListener("Selected",this._onSelected);
        this._rootElem.removeEventListener('click', this._onRowClick);
        this._rootElem.removeEventListener('dblclick', this._onRowDoubleClick);
        this._rootElem.removeEventListener('mouseenter', this._onMouseEnter);
        this._rootElem.removeEventListener('mouseleave', this._onMouseLeave);
    }
    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/
    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render(){
        let prom = new Promise((resolve/*,reject*/) => {
            this._setId(this._model.getId());
            this._setRowData(this._model.getRowData());
            resolve();
        });
        return prom;
    }
    get rootElement(){
        return this.getRootElement();
    }
    getRootElement(){
        return this._rootElem;
    }
    get model(){
        return this.getModel();
    }
    getModel(){
        return this._model;
    }
    /*******************************************/
    /* END COMPONENT RENDERER METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    /**
     * Fired when the unique key for the row has changed
     * @param newVal {string}
     * @private
     */
    _onId(newVal){
        this._setId(newVal);
    }

    /**
     * Fired when the row data has changed
     * @param newVal {object}
     * @private
     */
    _onRowData(newVal){
        this._setRowData(newVal);
    }

    /**
     * Fired when the grid column order has changed
     * @param newVal {array}
     * @private
     */
    _onColumnOrder(newVal){
        this._setColumnOrder(newVal);
    }

    /**
     * Fired when the grid columns have changed
     * @param newVal {object}
     * @private
     */
    _onColumnComponents(newVal){
        this._setColumns(newVal);
    }

    /**
     * Fired when a row has been selected
     * @param newVal {boolean}
     * @private
     */
    _onSelected(newVal){
        this._setSelected(newVal);
    }

    /**
     * Fired when a row has been selected by the user
     * @param event {event}
     * @private
     */
    _onRowClick(event){
        this._model._triggerRowUserSelected(event);
    }

    /**
     * Fired when a row has been selected by the user
     * @param event {event}
     * @private
     */
    _onRowDoubleClick(event){
        this._model._triggerRowUserDoubleClicked(event);
    }

    /**
     * Fired when the user has entered the row with the mouse
     * @param event {event}
     * @private
     */
    _onMouseEnter(/*event*/){
        this._rootElem.classList.add('active');
    }

    /**
     * Fired when the user has left the row with the mouse
     * @param event {event}
     * @private
     */
    _onMouseLeave(/*event*/){
        this._rootElem.classList.remove('active');
    }

    /**
     * Catch any actions emitted from the inner cells
     * @param eventData {object}
     * @private
     */
    _onCellAction(eventData){
        this._model._triggerRowAction(eventData);
    }

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE API METHODS */
    /*******************************************/

    /**
     * Set the row id in the DOM somewhere?
     * @param newVal {string}
     * @private
     */
    _setId(newVal){
        this._rootElem.id = newVal;
    }

    /**
     * Build the row display
     * @param newVal {object}
     * @private
     */
    _setRowData(newVal){
        this._cells.forEach((cell) => {
            cell.removeAllListeners('CellAction');
            cell.shutdown();
        });
        this._cells = [];
        this._setCentralColumn(false);
        let columnOrder = this._model.getColumnOrder(),
            columnComponents = this._model.getColumnComponents();
        if (columnOrder.length > 0) {
            columnOrder.forEach((columnKey) => {
                let centralColumn = columnComponents[columnKey].getCentralColumn();
                let cell = new GridCell({
                    id: columnKey,
                    columnRendererComponent: columnComponents[columnKey].getColumnRendererComponent(),
                    centralColumn: centralColumn,
                    value: newVal[columnKey],
                    width: columnComponents[columnKey].getWidth()
                });
                this._cells.push(cell);
                cell.addListener('CellAction', this._onCellAction = this._onCellAction.bind(this));
                if (centralColumn === true) {
                    this._setCentralColumn(true);
                }
                this._rootElem.appendChild(cell.renderer.rootElement);
            });
        } else {
            for (let columnKey in newVal) {
                let centralColumn = columnComponents[columnKey].getCentralColumn();
                let cell = new GridCell({
                    id: columnKey,
                    columnRendererComponent: columnComponents[columnKey].getColumnRendererComponent(),
                    centralColumn: centralColumn,
                    value: newVal[columnKey],
                    width: columnComponents[columnKey].getWidth()
                });
                this._cells.push(cell);
                cell.addListener('CellAction', this._onCellAction = this._onCellAction.bind(this));
                if (centralColumn === true) {
                    this._setCentralColumn(true);
                }
                this._rootElem.appendChild(cell.renderer.rootElement);
            }
        }
    }

    /**
     * Rebuild the row with the new column order
     * @param newVal {array}
     * @private
     */
    _setColumnOrder(/*newVal*/){
        this._setRowData(this._model.getRowData());
    }

    /**
     * Rebuild the row with the new columns
     * @param newVal {array}
     * @private
     */
    _setColumns(/*newVal*/){
        this._setRowData(this._model.getRowData());
    }

    /**
     * Toggle the central column state
     * @param newVal {boolean}
     * @private
     */
    _setCentralColumn(newVal){
        if (newVal === true) {
            this._rootElem.classList.add('has-central-column');
        } else {
            this._rootElem.classList.remove('has-central-column');
        }
    }

    /**
     * Select/Deselect the row
     * @param newVal {boolean}
     * @private
     */
    _setSelected(newVal){
        if (newVal === true) {
            this._rootElem.classList.add('selected');
        } else {
            this._rootElem.classList.remove('selected');
        }
    }

    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}

module.exports = GridRowRenderer;